import Button from "../../ui/Button/Button";
import AdminTableFiltersCard from "../ui/AdminTableFiltersCard";

const US_STATE_CODES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL", "GA", "HI", "ID",
  "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO",
  "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA",
  "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

type AdminUsersFiltersProps = {
  search: string;
  locationSearch: string;
  state: string;
  under18: boolean;
  skillSearch: string;
  role: string;
  accountType: string;
  teenPayoutSetup: string;
  status: string;
  emailVerified: string;
  guardianConnection: string;
  createdFromDate: string;
  createdToDate: string;
  pageSize: number;
  onSearchChange: (value: string) => void;
  onLocationSearchChange: (value: string) => void;
  onStateChange: (value: string) => void;
  onUnder18Change: (value: boolean) => void;
  onSkillSearchChange: (value: string) => void;
  onRoleChange: (value: string) => void;
  onAccountTypeChange: (value: string) => void;
  onTeenPayoutSetupChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onEmailVerifiedChange: (value: string) => void;
  onGuardianConnectionChange: (value: string) => void;
  onCreatedFromDateChange: (value: string) => void;
  onCreatedToDateChange: (value: string) => void;
  onPageSizeChange: (value: number) => void;
  onRequestTotalCount: () => void;
  onGenerateEmailList: () => void;
};

const AdminUsersFilters = ({
  search,
  locationSearch,
  state,
  under18,
  skillSearch,
  role,
  accountType,
  teenPayoutSetup,
  status,
  emailVerified,
  guardianConnection,
  createdFromDate,
  createdToDate,
  pageSize,
  onSearchChange,
  onLocationSearchChange,
  onStateChange,
  onUnder18Change,
  onSkillSearchChange,
  onRoleChange,
  onAccountTypeChange,
  onTeenPayoutSetupChange,
  onStatusChange,
  onEmailVerifiedChange,
  onGuardianConnectionChange,
  onCreatedFromDateChange,
  onCreatedToDateChange,
  onPageSizeChange,
  onRequestTotalCount,
  onGenerateEmailList,
}: AdminUsersFiltersProps) => {
  return (
    <AdminTableFiltersCard>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[280px] flex-[2_1_320px]">
          <label htmlFor="admin-users-filter-search" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Search
          </label>
          <input
            id="admin-users-filter-search"
            type="text"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Email, first name, last name"
            className="w-full rounded-lg border border-myteengig-border bg-white px-4 py-3 text-base text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          />
        </div>

        <div className="min-w-[240px] flex-[2_1_280px]">
          <label htmlFor="admin-users-filter-location-search" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Location
          </label>
          <input
            id="admin-users-filter-location-search"
            type="text"
            value={locationSearch}
            onChange={(event) => onLocationSearchChange(event.target.value)}
            placeholder="City, state, zip"
            className="w-full rounded-lg border border-myteengig-border bg-white px-4 py-3 text-base text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          />
        </div>

        <div className="min-w-[130px] flex-[1_1_150px]">
          <label htmlFor="admin-users-filter-state" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            State
          </label>
          <select
            id="admin-users-filter-state"
            value={state}
            onChange={(event) => onStateChange(event.target.value)}
            className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          >
            <option value="">All states</option>
            {US_STATE_CODES.map((code) => <option key={code} value={code}>{code}</option>)}
          </select>
        </div>

        <div className="min-w-[240px] flex-[2_1_280px]">
          <label htmlFor="admin-users-filter-skill-search" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Teen Skills
          </label>
          <input
            id="admin-users-filter-skill-search"
            type="text"
            value={skillSearch}
            onChange={(event) => onSkillSearchChange(event.target.value)}
            placeholder="Babysitting, tutoring, lawn care"
            className="w-full rounded-lg border border-myteengig-border bg-white px-4 py-3 text-base text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          />
        </div>

        <div className="min-w-[140px] flex-[1_1_160px]">
          <label htmlFor="admin-users-filter-role" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Role
          </label>
          <select
            id="admin-users-filter-role"
            value={role}
            onChange={(event) => onRoleChange(event.target.value)}
            className="h-[50px] rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          >
            <option value="">All roles</option>
            <option value="TEEN">TEEN</option>
            <option value="ADULT">ADULT</option>
            <option value="ADMIN_READ">ADMIN_READ</option>
            <option value="ADMIN_WRITE">ADMIN_WRITE</option>
          </select>
        </div>

        <label className="flex h-[50px] min-w-[180px] items-center gap-2 rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary">
          <input
            type="checkbox"
            checked={under18}
            onChange={(event) => onUnder18Change(event.target.checked)}
            className="h-4 w-4 accent-myteengig-primary"
          />
          Teens under 18
        </label>

        <div className="min-w-[150px] flex-[1_1_170px]">
          <label htmlFor="admin-users-filter-account-type" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Account
          </label>
          <select
            id="admin-users-filter-account-type"
            value={accountType}
            onChange={(event) => onAccountTypeChange(event.target.value)}
            className="h-[50px] rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          >
            <option value="">All accounts</option>
            <option value="PERSONAL">PERSONAL</option>
            <option value="BUSINESS">BUSINESS</option>
          </select>
        </div>

        <div className="min-w-[150px] flex-[1_1_170px]">
          <label htmlFor="admin-users-filter-teen-payout" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Stripe
          </label>
          <select
            id="admin-users-filter-teen-payout"
            value={teenPayoutSetup}
            onChange={(event) => onTeenPayoutSetupChange(event.target.value)}
            className="h-[50px] rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          >
            <option value="">Any</option>
            <option value="READY">Teen ready</option>
            <option value="NOT_READY">Teen not ready</option>
          </select>
        </div>

        <div className="min-w-[140px] flex-[1_1_160px]">
          <label htmlFor="admin-users-filter-status" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Status
          </label>
          <select
            id="admin-users-filter-status"
            value={status}
            onChange={(event) => onStatusChange(event.target.value)}
            className="h-[50px] rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          >
            <option value="">All statuses</option>
            <option value="PENDING">PENDING</option>
            <option value="ACTIVE">ACTIVE</option>
            <option value="SUSPENDED">SUSPENDED</option>
            <option value="DELETED">DELETED</option>
          </select>
        </div>

        <div className="min-w-[140px] flex-[1_1_160px]">
          <label htmlFor="admin-users-filter-email-verified" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Email
          </label>
          <select
            id="admin-users-filter-email-verified"
            value={emailVerified}
            onChange={(event) => onEmailVerifiedChange(event.target.value)}
            className="h-[50px] rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          >
            <option value="">Any</option>
            <option value="true">Verified</option>
            <option value="false">Unverified</option>
          </select>
        </div>

        <div className="min-w-[180px] flex-[1_1_220px]">
          <label htmlFor="admin-users-filter-guardian-connection" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Guardian/Teen Connection
          </label>
          <select
            id="admin-users-filter-guardian-connection"
            value={guardianConnection}
            onChange={(event) => onGuardianConnectionChange(event.target.value)}
            className="h-[50px] rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          >
            <option value="">Any</option>
            <option value="true">Has connection</option>
            <option value="false">No connection</option>
          </select>
        </div>

        <div className="min-w-[160px] flex-[1_1_180px]">
          <label htmlFor="admin-users-filter-created-from" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Created From
          </label>
          <input
            id="admin-users-filter-created-from"
            type="date"
            value={createdFromDate}
            onChange={(event) => onCreatedFromDateChange(event.target.value)}
            className="h-[50px] rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          />
        </div>

        <div className="min-w-[160px] flex-[1_1_180px]">
          <label htmlFor="admin-users-filter-created-to" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Created To
          </label>
          <input
            id="admin-users-filter-created-to"
            type="date"
            min={createdFromDate || undefined}
            value={createdToDate}
            onChange={(event) => onCreatedToDateChange(event.target.value)}
            className="h-[50px] rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          />
        </div>

        <div className="min-w-[110px] flex-[0_1_120px]">
          <label htmlFor="admin-users-filter-page-size" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Rows
          </label>
          <select
            id="admin-users-filter-page-size"
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            className="h-[50px] rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>
        </div>

        <div className="min-w-[180px] flex-[0_1_220px]">
          <Button type="button" variant="outline" className="h-[50px] w-full justify-center" onClick={onRequestTotalCount}>
            Get total count
          </Button>
        </div>

        <div className="min-w-[190px] flex-[0_1_240px]">
          <Button type="button" className="h-[50px] w-full justify-center" onClick={onGenerateEmailList}>
            Generate email list
          </Button>
        </div>
      </div>
    </AdminTableFiltersCard>
  );
};

export default AdminUsersFilters;
